import React, { useMemo, useState } from 'react';

import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { type FormattedTaskOption } from 'src/utils/restrictionUtils';
import { createSearchRegExp } from 'src/utils/searchUtils';

// **** Types **** //

interface TaskSelectorProps {
  availableOptions: FormattedTaskOption[];
  onSelectTask: (entityId: string) => void;
  selectedOption: string;
  isDisabled: boolean;
  /** @testOnly */
  index: number;
}

// **** Component **** //

/**
 * Select a "Task" from a dropdown list.
 */
const TaskSelector = ({ availableOptions, onSelectTask, selectedOption, isDisabled, index }: TaskSelectorProps) => {
  const [taskQuery, setTaskQuery] = useState<string>();

  // Apply user entered filter string
  const matchedTaskOptions = useMemo(() => {
    if (!taskQuery) {
      return availableOptions;
    }
    const regexSearch = createSearchRegExp(taskQuery);
    return availableOptions.filter((option) => !taskQuery || regexSearch.test(option.label));
  }, [availableOptions, taskQuery]);

  return (
    <Select
      data-cy={`new-jmr__restrictions__dropdown__${index}`}
      data-testid={`new-jmr__restrictions__dropdown__${index}`}
      searchQuery={taskQuery}
      onSearch={(v) => setTaskQuery(v)}
      placeholder="Select task"
      value={selectedOption}
      onChange={(v) => v !== selectedOption && onSelectTask(v)}
      disabled={isDisabled}
    >
      {matchedTaskOptions.map((option) => (
        <SelectOption
          label={option.label}
          value={option.value}
          key={option.value}
          data-cy="new-jmr__restrictions__dropdown-item"
        />
      ))}
      {!matchedTaskOptions.length && (
        <Column alignmentVertical="center" spacing="300" spacingInset="500">
          <Text alignment="center">No tasks found</Text>
        </Column>
      )}
    </Select>
  );
};

// **** Export **** //

export default TaskSelector;
